import { Remarkable } from "remarkable";
import { linkify } from "remarkable/linkify";
import {
  appendMemberUuidToUrl,
  isInternalServiceUrl,
} from "src/utils/internalServices";

// Whitelist of URL patterns that should open in-app without page reload
// These links will be handled by React Router instead of opening in a new tab
const IN_APP_ROUTE_PATTERNS = [
  /[?&]displayGlobalTask=/i, // Links with displayGlobalTask parameter (opens GlobalTask pane)
  /\/projects\/[0-9a-f-]+\/[0-9a-f-]+\?taskType=/i, // Links to tasks within projects (opens Task pane in SingleProject)
  /\/projects\/[0-9a-f-]+\/details/i, // Links to project details page
  /\/workspace\/[0-9a-f-]+\/projects\/[0-9a-f-]+\/profile\/[^/]+\/[^/?]+/i, // Links to creator profiles within a project
  // Add more patterns here as needed, e.g.:
  // /[?&]openPanel=/i,
];

/**
 * Check if a URL should be opened in-app based on whitelist patterns
 */
function isInAppRoute(url: string): boolean {
  if (!url) return false;

  return IN_APP_ROUTE_PATTERNS.some((pattern) => pattern.test(url));
}

// Open links rendered from assistant markdown in a new tab, mirroring the
// security attributes used by the shared ExternalLink component
// (src/app/components/ExternalLink/ExternalLink.tsx).
// However, whitelisted routes will open in-app without page reload.
const md = new Remarkable({
  html: false,
  typographer: false,
  breaks: true,
  linkTarget: "_blank",
});

// Remarkable 2.x dropped the `linkify` option; the plugin restores
// autoconversion of bare URLs into links.
md.use(linkify);

const defaultLinkOpen = md.renderer.rules.link_open;

md.renderer.rules.link_open = (tokens, idx, options, env, instance) => {
  const rendered = defaultLinkOpen(tokens, idx, options, env, instance);

  // Get the href attribute from the token
  const hrefAttr = tokens[idx].href || "";

  // Check if this is an in-app route
  if (isInAppRoute(hrefAttr)) {
    // For in-app routes, add a data attribute and remove target="_blank"
    return rendered
      .replace(/target="_blank"\s*/g, "") // Remove target="_blank"
      .replace(/^<a /, '<a data-in-app-route="true" ');
  }

  if (isInternalServiceUrl(hrefAttr)) {
    return rendered.replace(/^<a /, '<a rel="nofollow" ');
  }

  // For external links, keep the original behavior with security attributes
  return rendered.replace(/^<a /, '<a rel="noopener noreferrer nofollow" ');
};

/**
 * Render markdown to HTML. When `memberUuid` is provided, links to
 * knowledge base domains (help.indahash.com) are automatically augmented
 * with ?memberUuid=<uuid> so that Chatwoot KB can identify the user.
 */
export function renderMarkdown(text: string, memberUuid?: string): string {
  if (!text) return "";

  const html = md.render(text) as string;

  if (!memberUuid) return html;

  // Post-process: inject memberUuid into KB links.
  // We modify the href attribute of any <a> pointing to a KB domain.
  return html.replace(
    /<a\s+([^>]*?)href="([^"]*help\.indahash\.com[^"]*)"([^>]*)>/gi,
    (_match, before, href, after) => {
      const newHref = appendMemberUuidToUrl(href, memberUuid);
      return `<a ${before}href="${newHref}"${after}>`;
    },
  );
}
